import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { useRealtimeEvent } from '../contexts/RealtimeContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Wrench, AlertTriangle, User, Clock, Edit, RefreshCw } from 'lucide-react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ro } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

const DragAndDropCalendar = withDragAndDrop(Calendar);

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: ro }),
  getDay,
  locales: { 'ro': ro }
});

const CalendarPageEnhanced = () => {
  const { selectedPlant } = useData();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ scheduled_date: '', assigned_to: '' });
  const [isMobile, setIsMobile] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedPlant && selectedPlant !== 'all' ? { plant_id: selectedPlant } : {};
      const response = await api.get('/calendar/events', { params });
      
      const formattedEvents = response.data.map(event => ({
        id: event.id,
        title: `${event.equipment_code || 'N/A'} - ${event.type || 'Revizie'}`,
        start: new Date(event.date),
        end: new Date(event.date),
        resource: event,
        type: event.type,
        status: event.status,
        assignee: event.assigned_to_name
      }));
      
      setEvents(formattedEvents);
    } catch (error) {
      toast.error('Eroare la incarcare evenimente');
    } finally {
      setLoading(false);
    }
  }, [selectedPlant]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.filter(u => u.role === 'technician' || u.role === 'manager'));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchEventsQuiet = useCallback(async () => {
    try {
      const params = selectedPlant && selectedPlant !== 'all' ? { plant_id: selectedPlant } : {};
      const response = await api.get('/calendar/events', { params });
      const formattedEvents = response.data.map(event => ({
        id: event.id,
        title: `${event.equipment_code || 'N/A'} - ${event.type || 'Revizie'}`,
        start: new Date(event.date), end: new Date(event.date),
        resource: event, type: event.type, status: event.status, assignee: event.assigned_to_name
      }));
      setEvents(formattedEvents);
    } catch {}
  }, [selectedPlant]);

  // Real-time: refresh calendar on PM changes, ticket updates
  useRealtimeEvent('pm_change', fetchEventsQuiet);
  useRealtimeEvent('new_ticket', fetchEventsQuiet);
  useRealtimeEvent('ticket_update', fetchEventsQuiet);

  useEffect(() => {
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    fetchEvents();
    fetchUsers();
  }, [selectedPlant, fetchEvents, fetchUsers]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setEditForm({
      scheduled_date: format(event.start, 'yyyy-MM-dd'),
      assigned_to: event.resource.assigned_to || ''
    });
    setShowEditDialog(true);
  };

  const handleEventDrop = async ({ event, start }) => {
    try {
      const newDate = format(start, 'yyyy-MM-dd');
      
      await api.patch(`/revisions/${event.id}/reschedule`, null, {
        params: {
          scheduled_date: newDate
        }
      });
      
      toast.success('Revizie reprogramata!');
      fetchEvents();
    } catch (error) {
      toast.error('Eroare la reprogramare');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.patch(`/revisions/${selectedEvent.id}/reschedule`, null, {
        params: editForm
      });
      
      toast.success('Revizie actualizata!');
      setShowEditDialog(false);
      fetchEvents();
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#3B82F6'; // blue
    
    if (event.status === 'completed') {
      backgroundColor = '#10B981'; // green
    } else if (event.status === 'in_progress') {
      backgroundColor = '#F59E0B'; // orange
    } else if (event.status === 'overdue') {
      backgroundColor = '#EF4444'; // red
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        padding: '2px 5px'
      }
    };
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Calendar Mentenanta</h1>
          <p className="text-slate-600 mt-1">Planificare revizii cu drag & drop</p>
        </div>
        <Button onClick={fetchEvents} variant="outline" className="h-10 lg:h-auto">
          <RefreshCw className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Reincarca</span>
        </Button>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3 lg:gap-6">
            <p className="text-sm font-medium text-slate-700 w-full sm:w-auto">Legenda:</p>
            <Badge className="bg-blue-600">Planificat</Badge>
            <Badge className="bg-orange-500">In Lucru</Badge>
            <Badge className="bg-green-500">Completat</Badge>
            <Badge className="bg-red-500">Intarziat</Badge>
            <p className="text-xs text-slate-500 w-full sm:w-auto sm:ml-auto">Click eveniment pentru edit | Drag & drop pentru reprogramare</p>
          </div>
        </CardContent>
      </Card>

      {/* Calendar - Mobile Optimized */}
      <Card>
        <CardContent className="p-2 lg:p-6">
          <div className="overflow-x-auto">
            <div style={{ minWidth: '800px', height: 600 }}>
              <DragAndDropCalendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={handleSelectEvent}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventDrop}
                eventPropGetter={eventStyleGetter}
                views={['month', 'week', 'day']}
                defaultView="month"
                draggableAccessor={() => true}
                resizable={true}
                selectable={true}
                culture="ro"
                messages={{
                  next: 'Urmatoare',
                  previous: 'Anterioara',
                  today: 'Azi',
                  month: 'Luna',
                  week: 'Saptamana',
                  day: 'Zi'
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editeaza Revizie</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-slate-900">{selectedEvent.title}</p>
                <p className="text-sm text-slate-500 mt-1">
                  Echipament: {selectedEvent.resource?.equipment_name}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Data Programata</Label>
                <Input
                  type="date"
                  value={editForm.scheduled_date}
                  onChange={(e) => setEditForm({...editForm, scheduled_date: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Asignat Catre</Label>
                <Select
                  value={editForm.assigned_to}
                  onValueChange={(v) => setEditForm({...editForm, assigned_to: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteaza tehnician" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSaveEdit} className="btn-primary">
              <Edit className="w-4 h-4 mr-2" />
              Salveaza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPageEnhanced;